import { useCallback, useEffect, useState } from 'react'
import { get } from './lib/api'
import { me, signOut, type User } from './lib/auth'
import { t } from './lib/i18n'
import SignIn from './components/SignIn'
import Account from './components/Account'
import Admin from './components/Admin'
import EnvBanner from './components/EnvBanner'
import UpdateBar from './components/UpdateBar'
import InstallPrompt from './components/InstallPrompt'
import DebugPanel from './components/DebugPanel'
import ErrorBoundary from './components/ErrorBoundary'
import Sailings from './components/Sailings'
import Book from './components/Book'
import Pass from './components/Pass'
import Residency from './components/Residency'
import Gate from './components/Gate'
import Ops, { Today } from './components/Ops'
import CrewReporter from './components/CrewReporter'
import MyTrips from './components/MyTrips'
import Checkout from './components/Checkout'
import { ferry, type Me, type Sailing, type Trip } from './lib/ferry'
import Icon from './components/Icon'
import LanguageToggle from './components/LanguageToggle'
import PreviewNotice from './components/PreviewNotice'
import Passport, { PassportStrip } from './components/Passport'
import Seascape from './components/Seascape'
import ThemeToggle from './components/ThemeToggle'

export interface Health {
  app: string
  environment: 'local' | 'beta' | 'production'
  version: string
  commit: string
}

type Tab = 'home' | 'trips' | 'account' | 'gate' | 'ops' | 'report' | 'admin'

/** Where the passenger flow currently is. Booking is a path, not a tab. */
type Flow =
  | { at: 'browse' }
  | { at: 'book'; sailing: Sailing }
  | { at: 'pay'; bookingId: string; totalCents: number; reference: string }
  | { at: 'pass'; bookingId: string }

export default function App() {
  const [user, setUser] = useState<User | null>(null)
  const [health, setHealth] = useState<Health | null>(null)
  const [ready, setReady] = useState(false)
  const [tab, setTab] = useState<Tab>('home')
  const [flow, setFlow] = useState<Flow>({ at: 'browse' })
  const [ferryMe, setFerryMe] = useState<Me | null>(null)
  // Drives the badge on My Trips: a count, and whether anything is urgent.
  const [tripCount, setTripCount] = useState<{ remaining: number; needsAttention: boolean }>({ remaining: 0, needsAttention: false })

  const refreshFerry = useCallback(() => {
    ferry.me().then(setFerryMe).catch(() => setFerryMe(null))
    ferry.trips()
      .then((v) => setTripCount({ remaining: v.remaining, needsAttention: v.needsAttention }))
      .catch(() => setTripCount({ remaining: 0, needsAttention: false }))
  }, [])

  const refresh = useCallback(async () => {
    setUser(await me())
  }, [])

  // The ferry profile decides fares, seat choice and vehicles, so it is fetched
  // whenever the session changes rather than by each screen that needs it.
  useEffect(() => {
    if (user) refreshFerry()
    else setFerryMe(null)
  }, [user, refreshFerry])

  useEffect(() => {
    void (async () => {
      // Both in parallel: neither depends on the other, and the boot is the one
      // moment the user is definitely waiting.
      const [u, h] = await Promise.all([
        me(),
        get<Health>('/api/health').catch(() => null),
      ])
      setUser(u)
      setHealth(h)
      setReady(true)
    })()
  }, [])

  if (!ready) return <div className="boot">{t('app.loading')}</div>

  return (
    <ErrorBoundary>
      <Seascape />
      {/* Above everything: an app running old code is the cause of bug reports
          nobody can reproduce. */}
      <UpdateBar />
      <EnvBanner health={health} />
      {/* Production is the only environment a stranger can reach. */}
      <PreviewNotice environment={health?.environment ?? ''} />

      <header className="bar">
        <strong className="brand"><Icon name="ferry" size={20} />{health?.app ?? 'Ferry'}</strong>
        {user && (
          <nav>
            <button className="with-icon" onClick={() => { setTab('home'); setFlow({ at: 'browse' }) }} aria-current={tab === 'home'}>
              <Icon name="ferry" size={17} />{t('nav.book')}
            </button>
            <button
              className={`with-icon${tripCount.needsAttention ? ' attention' : ''}`}
              onClick={() => { setTab('trips'); setFlow({ at: 'browse' }) }}
              aria-current={tab === 'trips'}
            >
              <Icon name="ticket" size={17} />{t('nav.trips')}
              {tripCount.remaining > 0 && <span className="badge-count">{tripCount.remaining}</span>}
            </button>
            <button className="with-icon" onClick={() => setTab('account')} aria-current={tab === 'account'}>
              <Icon name="person" size={17} />{t('nav.account')}
            </button>
            {/* Staff screens, by capability — the tab is absent rather than denied. */}
            {user.capabilities.includes('users.read') && (
              <button className="with-icon" onClick={() => setTab('gate')} aria-current={tab === 'gate'}>
                <Icon name="scan" size={17} />{t('nav.gate')}
              </button>
            )}
            {user.capabilities.includes('users.read') && (
              <button className="with-icon" onClick={() => setTab('ops')} aria-current={tab === 'ops'}>
                <Icon name="chart" size={17} />{t('nav.ops')}
              </button>
            )}
            {user.capabilities.includes('users.read') && (
              <button className="with-icon" onClick={() => setTab('report')} aria-current={tab === 'report'}>
                <Icon name="route" size={17} />{t('nav.report')}
              </button>
            )}
            {/* Capability-driven, so the tab is absent rather than present-and-denied. */}
            {user.capabilities.includes('admin.access') && (
              <button className="with-icon" onClick={() => setTab('admin')} aria-current={tab === 'admin'}>
                <Icon name="settings" size={17} />{t('admin.title')}
              </button>
            )}
            <button onClick={async () => { await signOut(); await refresh() }}>{t('app.signOut')}</button>
          </nav>
        )}
        <LanguageToggle />
        <ThemeToggle />
      </header>

      <main>
        {/*
          * The schedule is shown to EVERYONE, signed in or not. Somebody
          * standing on the dock wanting to know where the boat is should not
          * meet a sign-in wall — and an app that answers that question is an app
          * they keep, which is the only reason they will ever book in it.
          */}
        {(!user || (tab === 'home' && flow.at === 'browse')) && (
          <PassportStrip signedIn={!!user} onOpen={() => { setTab('trips'); setFlow({ at: 'browse' }) }} />
        )}
        {(!user || (tab === 'home' && flow.at === 'browse')) && (
          <Sailings
            canBook={!!user}
            onBook={(sailing) => setFlow({ at: 'book', sailing })}
          />
        )}
        {user && tab === 'home' && flow.at === 'book' && (
          <Book
            sailing={flow.sailing}
            me={ferryMe}
            onCancel={() => setFlow({ at: 'browse' })}
            /* Booking holds the seats; payment is the next screen, not a
               side-effect of pressing Book. */
            onDone={(bookingId, totalCents, reference) =>
              setFlow({ at: 'pay', bookingId, totalCents, reference })}
          />
        )}
        {user && flow.at === 'pay' && (
          <Checkout
            bookingId={flow.bookingId}
            totalCents={flow.totalCents}
            reference={flow.reference}
            onBack={() => setFlow({ at: 'browse' })}
            onPaid={() => { refreshFerry(); setFlow({ at: 'pass', bookingId: flow.bookingId }) }}
          />
        )}
        {user && flow.at === 'pass' && (
          <Pass bookingId={flow.bookingId} onBack={() => { refreshFerry(); setFlow({ at: 'browse' }) }} />
        )}

        {user && tab === 'trips' && flow.at === 'browse' && (
          <>
            <MyTrips
              onPass={(bookingId) => setFlow({ at: 'pass', bookingId })}
              onPay={(trip: Trip) => setFlow({
                at: 'pay', bookingId: trip.id, totalCents: trip.total_cents, reference: trip.reference,
              })}
            />
            {/* Under the trips, never above them: the next boat is why they came. */}
            <Passport />
          </>
        )}

        {!user && <SignIn onSignedIn={refresh} />}

        {user && tab === 'account' && (
          <>
            <Residency me={ferryMe} onChanged={refreshFerry} />
            <Account user={user} />
          </>
        )}
        {user && tab === 'gate' && <Gate />}
        {user && tab === 'ops' && (
          <>
            <section className="card">
              <h2>Today</h2>
              <Today />
            </section>
            <Ops />
          </>
        )}
        {user && tab === 'report' && <CrewReporter />}
        {user && tab === 'admin' && <Admin />}
      </main>

      {/* Capability, not environment: the phone that needs diagnosing is always
          a real one in production. */}
      {user?.capabilities.includes('errors.read') && <DebugPanel environment={health?.environment ?? '—'} />}

      {/* Below the fold, not in the way. For an app that is not on a store yet. */}
      <InstallPrompt />

      <footer className="muted small">
        {health ? `${health.version} · ${health.commit} · ${health.environment}` : ''}
      </footer>
    </ErrorBoundary>
  )
}
